import { useQuery } from '@tanstack/react-query';
import { fetchDocumentBaseCss } from './templateStylesApi.ts';
import {
  injectDocumentBaseCss,
  readDocumentBaseCache,
  writeDocumentBaseCache,
} from './documentBaseStyles.ts';

// Loads the canonical document base CSS and injects the live copy into `<head>`
// (replacing the bundled fallback injected on module import). Called from
// `RichTextEditor` — the only place templates are actually edited, and a
// surface that is always authenticated — so the fetch runs exactly where it is
// needed, never app-wide. React Query dedupes across mounted instances, so it
// is effectively one fetch per session.
//
// Caching mirrors the endpoint contract:
//  - the previously seen ETag + CSS live in localStorage (warm start across
//    reloads — the module-load inject already used it for first paint),
//  - the request sends `If-None-Match`, so a refetch is usually a 304 with no
//    transfer and we keep the cached copy,
//  - a 200 (new deploy with different CSS) rewrites the cache and re-injects.
export const useDocumentBaseStyles = () =>
  useQuery({
    queryKey: ['template-styles', 'document-css'],
    queryFn: async () => {
      const cached = readDocumentBaseCache();
      const result = await fetchDocumentBaseCss(cached?.etag);

      if (result.notModified) {
        if (cached?.css) {
          injectDocumentBaseCss(cached.css);
        }
        return cached?.css ?? null;
      }

      const css = result.css ?? '';
      writeDocumentBaseCache({ etag: result.etag ?? null, css });
      injectDocumentBaseCss(css);
      return css;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
  });
